import { sha256 } from '@noble/hashes/sha256';
import { SignedDelegate } from './actions';
import { createTransaction } from './create_transaction';
import { encodeDelegateAction, encodeTransaction, SignedTransaction, Transaction } from './schema';
import { Signature } from './signature';
import { KeyType } from '@near-js/crypto';
/**
 * Signs a given transaction from an account with given keys, applied to the given network
 * @param transaction The Transaction object to sign
 * @param signer The {Signer} object that assists with signing keys
 * @param accountId The human-readable NEAR account name
 * @param networkId The targeted network. (ex. default, betanet, etc…)
 */
async function signTransactionObject(transaction, signer, accountId, networkId) {
    const message = encodeTransaction(transaction);
    const hash = new Uint8Array(sha256(message));
    const signature = await signer.signMessage(message, accountId, networkId);
    const keyType = transaction.publicKey.ed25519Key ? KeyType.ED25519 : KeyType.SECP256K1;
    const signedTx = new SignedTransaction({
        transaction,
        signature: new Signature({ keyType, data: signature.signature })
    });
    return [hash, signedTx];
}
export async function signTransaction(...args) {
    if (args[0].constructor === Transaction) {
        const [transaction, signer, accountId, networkId] = args;
        return signTransactionObject(transaction, signer, accountId, networkId);
    }
    else {
        const [receiverId, nonce, actions, blockHash, signer, accountId, networkId] = args;
        const publicKey = await signer.getPublicKey(accountId, networkId);
        const transaction = createTransaction(accountId, publicKey, receiverId, nonce, actions, blockHash);
        return signTransactionObject(transaction, signer, accountId, networkId);
    }
}
/**
 * Sign a delegate action
 * @options SignDelegate options
 * @param options.delegateAction Delegate action to be signed by the meta transaction sender
 * @param options.signer Signer instance for the meta transaction sender
 */
export async function signDelegateAction({ delegateAction, signer }) {
    const message = encodeDelegateAction(delegateAction);
    const signature = await signer.sign(message);
    const keyType = delegateAction.publicKey.ed25519Key ? KeyType.ED25519 : KeyType.SECP256K1;
    const signedDelegateAction = new SignedDelegate({
        delegateAction,
        signature: new Signature({
            keyType,
            data: signature,
        }),
    });
    return {
        hash: new Uint8Array(sha256(message)),
        signedDelegateAction,
    };
}
